/**
 * 响应式状态管理Hook
 * 提供设备信息和viewport变化的响应式状态
 */
import { useCallback, useEffect, useState } from 'react';

import { debounce, getDeviceInfo, getViewportInfo } from '@/utils/deviceDetection';
import type { DeviceInfo, ViewportInfo } from '@/utils/deviceDetection';

// 响应式状态接口
export interface ResponsiveState {
  device: DeviceInfo;
  viewport: ViewportInfo;
  isMobileMenuOpen: boolean;
}

/**
 * 响应式Hook - 监听设备和viewport变化
 */
export function useResponsive() {
  const [state, setState] = useState<ResponsiveState>(() => ({
    device: getDeviceInfo(),
    viewport: getViewportInfo(),
    isMobileMenuOpen: false,
  }));

  // 更新设备和viewport信息
  const updateResponsiveState = useCallback(() => {
    setState((prev) => ({
      ...prev,
      device: getDeviceInfo(),
      viewport: getViewportInfo(),
    }));
  }, []);

  // 防抖的resize处理函数
  const debouncedUpdate = useCallback(debounce(updateResponsiveState, 150), [
    updateResponsiveState,
  ]);

  // 监听窗口大小变化
  useEffect(() => {
    if (typeof window === 'undefined') return;

    window.addEventListener('resize', debouncedUpdate);
    window.addEventListener('orientationchange', debouncedUpdate);

    return () => {
      window.removeEventListener('resize', debouncedUpdate);
      window.removeEventListener('orientationchange', debouncedUpdate);
    };
  }, [debouncedUpdate]);

  // 切换移动端菜单状态
  const toggleMobileMenu = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isMobileMenuOpen: !prev.isMobileMenuOpen,
    }));
  }, []);

  // 关闭移动端菜单
  const closeMobileMenu = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isMobileMenuOpen: false,
    }));
  }, []);

  // 当切换到桌面端时自动关闭移动菜单
  useEffect(() => {
    if (state.viewport.isDesktop && state.isMobileMenuOpen) {
      closeMobileMenu();
    }
  }, [state.viewport.isDesktop, state.isMobileMenuOpen, closeMobileMenu]);

  return {
    ...state,
    toggleMobileMenu,
    closeMobileMenu,
    // 便捷的布尔值
    isMobile: state.viewport.isMobile,
    isTablet: state.viewport.isTablet,
    isDesktop: state.viewport.isDesktop,
    isTouchDevice: state.device.touchSupport,
    // 断点检查函数
    isBreakpoint: (breakpoint: ViewportInfo['breakpoint']) =>
      state.viewport.breakpoint === breakpoint,
  };
}

/**
 * 简化的移动端检测Hook
 */
export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 479;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const checkMobile = debounce(() => {
      setIsMobile(window.innerWidth <= 479);
    }, 150);

    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
}

/**
 * 断点匹配Hook
 */
export function useBreakpoint(breakpoint: ViewportInfo['breakpoint']) {
  const { viewport } = useResponsive();
  return viewport.breakpoint === breakpoint;
}

/**
 * 媒体查询Hook
 */
export function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [query]);

  return matches;
}
